import React, { useEffect, useState } from 'react';
import { 
  BrowserRouter as Router, 
  Route, 
  Routes, 
  Navigate, 
  useNavigate, 
  useLocation
} from 'react-router-dom';
import Login from './pages/Login';
import Home2 from './pages/Home2';
import Contact from './pages/Contact';
import Newsletter from './pages/Newsletter';
import Faq from './components/Faq'; 
import InfoNieuw from './pages/InfoNieuw';
import Hhr from './pages/Hhr';
import Energie from './pages/Energie';
import IstaAdminPanel from './components/IstaAdminPanel';
import Beheerdersportaal from './pages/Beheerdersportaal';
import Bewonersbeheer from './pages/Bewonersbeheer';
import KenterAdmin from './pages/KenterAdmin';
import DegreeDaysPage from './pages/DegreeDaysPage';
import AuthStatus from './components/AuthStatus';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import FaqAdmin from './pages/FaqAdmin';
import AnalyticsTracker from './components/AnalyticsTracker';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import VoorschotBeheer from './pages/VoorschotBeheer';
import EnergyAnalysis from './pages/EnergyAnalysis';
import UserManagement from './pages/UserManagement';
import InspectionWizard from './pages/InspectionWizard';
import OpnamestaatHandleiding from './pages/OpnamestaatHandleiding';
import OpnamestatenVragenbeheer from './pages/OpnamestatenVragenbeheer';
import OpnamestatenOverzicht from './pages/OpnamestatenOverzicht';
import BorstweringenPlanning from './pages/BorstweringenPlanning';
import BorstweringenAdmin from './pages/BorstweringenAdmin';
import BorstweringenInventarisatie from './pages/BorstweringenInventarisatie';
import BorstweringenInventarisatieAdmin from './pages/BorstweringenInventarisatieAdmin';
import CvControle from './pages/CvControle';
import CvControleAdmin from './pages/CvControleAdmin';
import ProjectPlanning from './pages/ProjectPlanning';
import KlusPlanning from './pages/KlusPlanning';
import SchoonmaakPortaal from './pages/SchoonmaakPortaal';
import SchoonmaakAdmin from './pages/SchoonmaakAdmin';
import ProjectPlanningAdmin from './pages/ProjectPlanningAdmin';
import ProjectPlanningDetail from './pages/ProjectPlanningDetail';
import SchilderplanningBalkons from './pages/SchilderplanningBalkons';
import SchilderPortaal from './pages/SchilderPortaal';
import Beoordeling from './pages/Beoordeling';
import { ToastProvider } from './components/ToastContext';
import { isPathAllowed, firstAllowedPath, getStoredAllowedPages } from './config/pages';


// Wrapper that uses the hooks and passes props to the actual component
const PrivateRouteWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <PrivateRouteComponent 
      navigate={navigate} 
      location={location}
    >
      {children}
    </PrivateRouteComponent>
  );
};

// The actual component that implements the logic
const PrivateRouteComponent = ({ children, navigate, location }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  useEffect(() => {
    // Simple token check - no API call to avoid delays
    const token = localStorage.getItem('token');
    
    if (!token) {
      // User has no token, redirect to home (not login)
      navigate('/home', { replace: true });
      return;
    }
    
    // We have a token, check if it's expired
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    const currentTime = Date.now();
    const tokenExpiry = loginTimestamp ? parseInt(loginTimestamp) + 3600000 : 0; // 1 hour expiry
    
    if (tokenExpiry && currentTime > tokenExpiry) {
      // Token is expired, try to refresh
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        // We have a refresh token, try to get a new token
        fetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken })
        })
        .then(response => response.json())
        .then(data => {
          if (data.accessToken) {
            // Got a new token, keep the authentication
            localStorage.setItem('token', data.accessToken);
            localStorage.setItem('loginTimestamp', Date.now().toString());
            if (enforcePageAccess()) setIsAuthenticated(true);
          } else {
            // Failed to refresh, redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('loginTimestamp');
            navigate('/login', { state: { from: location.pathname } });
          }
        })
        .catch(() => {
          // Error refreshing, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('loginTimestamp');
          navigate('/login', { state: { from: location.pathname } });
        })
        .finally(() => {
          setLoading(false);
        });
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('loginTimestamp');
        navigate('/login', { state: { from: location.pathname } });
        setLoading(false);
      }
    } else {
      // Token exists and not expired
      if (enforcePageAccess()) setIsAuthenticated(true);
      setLoading(false);
    }
  }, [navigate, location.pathname]);

  // Controleer of de ingelogde gebruiker de huidige pagina mag zien.
  // Whitelist-gebruikers (o.a. huismeester) worden teruggestuurd naar hun eigen pagina.
  // Geeft true terug als toegang OK is, false als er is omgeleid.
  function enforcePageAccess() {
    const allowedPages = getStoredAllowedPages();
    if (isPathAllowed(location.pathname, allowedPages)) {
      return true;
    }
    navigate(firstAllowedPath(allowedPages), { replace: true });
    return false;
  }
  
  // During initial load, just render a minimal placeholder
  if (loading) {
    return <div className="auth-loading" />;
  }
  
  // If authenticated, render the children
  if (isAuthenticated) {
    return children;
  }
  
  // This should not be reached normally, as the useEffect should handle redirects
  return null;
};

// Public route component - handles redirection for authenticated users
const PublicRoute = ({ children }) => {
  return children; // Always render children for public routes
};

function App() {
  // Setup automatic token refresh/logout
  useEffect(() => {
    const setupTokenRefresh = () => {
      // Check for token expiration every minute
      const interval = setInterval(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Check if token has expired (1 hour = 3600000 ms)
        // Use a conservative 55 minutes to ensure refresh before expiry
        const loginTimestamp = localStorage.getItem('loginTimestamp');
        if (!loginTimestamp) return;
        
        const tokenAge = Date.now() - parseInt(loginTimestamp);
        if (tokenAge > 3300000) { // 55 minutes
          // Token is about to expire, try to refresh
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            // No refresh token, just log out
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('loginTimestamp');
            window.location.href = '/home'; // Changed to /home instead of /login
            return;
          }
          
          // Try to refresh the token
          fetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
          })
          .then(response => response.json())
          .then(data => {
            if (data.accessToken) {
              localStorage.setItem('token', data.accessToken);
              localStorage.setItem('loginTimestamp', Date.now());
            } else {
              // Refresh failed, log out to home page
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('loginTimestamp');
              window.location.href = '/home'; // Changed to /home
            }
          })
          .catch(() => {
            // Error refreshing, log out to home page
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('loginTimestamp');
            window.location.href = '/home'; // Changed to /home
          });
        }
      }, 60000); // Check every minute
      
      return () => clearInterval(interval);
    };
    
    return setupTokenRefresh();
  }, []);

  // Check if user is on an admin page
  const isAdminPage = () => {
    const adminPaths = ['/beheer', '/newsletter', '/energie', '/ista-admin', '/bewoners', '/kenter-admin', '/degree-days', '/voorschotten', '/opnamestaten', '/opnamestaten-vragen', '/opnamestaten-overzicht', '/schilderwerk'];
    const currentPath = window.location.pathname;
    return adminPaths.some(path => currentPath.startsWith(path));
  };
  
  return (
    <Router>
      <ToastProvider>
        <AnalyticsTracker>
          {/* Show AuthStatus only on admin pages */}
          {isAdminPage() && <AuthStatus />}

          {/* SessionTimeoutWarning will show itself when needed */}
          <SessionTimeoutWarning />

      <Routes>
        <Route path="/" element={<Navigate to="/home" />} />

        {/* Public Routes */}
        <Route path="/home" element={<PublicRoute><Home2 /></PublicRoute>} />
        <Route path="/faq" element={<PublicRoute><Faq /></PublicRoute>} />
        <Route path="/infonieuw" element={<PublicRoute><InfoNieuw /></PublicRoute>} />
        <Route path="/hhr" element={<PublicRoute><Hhr /></PublicRoute>} />
        <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
        <Route path="/login" element={<Login />} />

        {/* Inspection Wizard - Public with token-based auth */}
        <Route path="/opnamestaat/:token" element={<InspectionWizard />} />

        {/* Handleiding Online Opnamestaat — verborgen publieke pagina (niet in menu) */}
        <Route path="/handleiding-opnamestaat" element={<PublicRoute><OpnamestaatHandleiding /></PublicRoute>} />

        {/* Borstweringen Planning - Public landing page */}
        <Route path="/borstweringen" element={<BorstweringenPlanning />} />

        {/* Borstweringen Inventarisatie - Public landing page */}
        <Route path="/borstweringen-inventarisatie" element={<BorstweringenInventarisatie />} />

        {/* CV-controle radiatoren en leidingen - Public landing page */}
        <Route path="/cv-controle" element={<CvControle />} />

        {/* Generic Project Planning - Public landing page */}
        <Route path="/planning/:slug" element={<ProjectPlanning />} />

        {/* Klusjesman uitvoer-overzicht - gedeelde link met token (geen login) */}
        <Route path="/klus/:token" element={<KlusPlanning />} />

        {/* Schoonmaak-checklist leverancier - gedeelde link met token (geen login) */}
        <Route path="/schoonmaak/:token" element={<SchoonmaakPortaal />} />

        {/* Bewonersbeoordeling schilderwerk - publieke tokenlink (geen login) */}
        <Route path="/beoordeling/:token" element={<Beoordeling />} />

        {/* Protected Routes */}
        <Route path="/newsletter" element={
          <PrivateRouteWrapper>
            <Newsletter />
          </PrivateRouteWrapper>
        } />
        <Route path="/energie" element={
          <PrivateRouteWrapper>
            <Energie />
          </PrivateRouteWrapper>
        } />
        <Route path="/ista-admin" element={
          <PrivateRouteWrapper>
            <IstaAdminPanel />
          </PrivateRouteWrapper>
        } />
        <Route path="/beheer" element={
          <PrivateRouteWrapper>
            <Beheerdersportaal />
          </PrivateRouteWrapper>
        } />
        <Route path="/schoonmaak-admin" element={
          <PrivateRouteWrapper>
            <SchoonmaakAdmin />
          </PrivateRouteWrapper>
        } />
        <Route path="/bewoners" element={
          <PrivateRouteWrapper>
            <Bewonersbeheer />
          </PrivateRouteWrapper>
        } />
        <Route path="/kenter-admin" element={
          <PrivateRouteWrapper>
            <KenterAdmin />
          </PrivateRouteWrapper>
        } />
        <Route path="/degree-days" element={
          <PrivateRouteWrapper>
            <DegreeDaysPage />
          </PrivateRouteWrapper>
        } />
        <Route path="/voorschotbeheer" element={
          <PrivateRouteWrapper>
            <VoorschotBeheer />
          </PrivateRouteWrapper>
        } />

<Route path="/faq-admin" element={
  <PrivateRouteWrapper>
    <FaqAdmin />
  </PrivateRouteWrapper>
} />

<Route path="/energy-analysis" element={
  <PrivateRouteWrapper>
    <EnergyAnalysis />
  </PrivateRouteWrapper>
} />

<Route path="/user-management" element={
  <PrivateRouteWrapper>
    <UserManagement />
  </PrivateRouteWrapper>
} />

<Route path="/opnamestaten" element={
  <PrivateRouteWrapper>
    <OpnamestatenOverzicht />
  </PrivateRouteWrapper>
} />

<Route path="/opnamestaten-vragen" element={
  <PrivateRouteWrapper>
    <OpnamestatenVragenbeheer />
  </PrivateRouteWrapper>
} />

<Route path="/opnamestaten-overzicht" element={
  <PrivateRouteWrapper>
    <OpnamestatenOverzicht />
  </PrivateRouteWrapper>
} />

<Route path="/analytics" element={
            <PrivateRouteWrapper>
              <AnalyticsDashboard />
            </PrivateRouteWrapper>
          } />

<Route path="/borstweringen-admin" element={
  <PrivateRouteWrapper>
    <BorstweringenAdmin />
  </PrivateRouteWrapper>
} />

<Route path="/borstweringen-inventarisatie-admin" element={
  <PrivateRouteWrapper>
    <BorstweringenInventarisatieAdmin />
  </PrivateRouteWrapper>
} />

<Route path="/cv-controle-admin" element={
  <PrivateRouteWrapper>
    <CvControleAdmin />
  </PrivateRouteWrapper>
} />

<Route path="/planning-admin" element={
  <PrivateRouteWrapper>
    <ProjectPlanningAdmin />
  </PrivateRouteWrapper>
} />

<Route path="/planning-admin/:id" element={
  <PrivateRouteWrapper>
    <ProjectPlanningDetail />
  </PrivateRouteWrapper>
} />

<Route path="/schilderwerk" element={
  <PrivateRouteWrapper>
    <SchilderplanningBalkons />
  </PrivateRouteWrapper>
} />

<Route path="/schilder" element={
  <PrivateRouteWrapper>
    <SchilderPortaal />
  </PrivateRouteWrapper>
} />

        {/* Catch All */}
        <Route path="*" element={<Navigate to="/home" />} />
      </Routes>
        </AnalyticsTracker>
      </ToastProvider>
    </Router>
  );
}

export default App;